"use client";

import React, { useState, useEffect, Fragment } from 'react';
import { Loader2, Video, Save, ChevronDown, Check } from 'lucide-react';
import { Listbox, Transition } from '@headlessui/react';
import { Project } from '@/types/project.interface';
import Api from '@/lib/api';
import { useModal } from '@/redux/useModal';
import { Group } from '@/types/group.interface';

interface CreationHeaderProps {
    currentGroup: Group | null;
    setCurrentGroup: React.Dispatch<React.SetStateAction<Group | null>>;
    setGroups: React.Dispatch<React.SetStateAction<Group[]>>;
    isSegmentsDirty: boolean;
    isGenerating: boolean;
    setIsGenerating: (generating: boolean) => void;
    setIsConfirmModalOpen: (open: boolean) => void;
}

export const CreationHeader: React.FC<CreationHeaderProps> = ({
    currentGroup,
    setCurrentGroup,
    setGroups,
    isSegmentsDirty,
    isGenerating,
    setIsGenerating,
    setIsConfirmModalOpen,
}) => {
    const modaler = useModal();
    const [localName, setLocalName] = useState(currentGroup?.name || "");

    useEffect(() => {
        setLocalName(currentGroup?.name || "");
    }, [currentGroup?.id, currentGroup?.name]);

    const hasChanges = currentGroup && (localName !== currentGroup.name);

    const handleUpdateGroup = async (data: Partial<Group>) => {
        if (!currentGroup) return;
        try {
            const updated = await Api.groups.update(currentGroup.id, data);
            setCurrentGroup(updated);
            setGroups(prev => prev.map(g => g.id === updated.id ? updated : g));
        } catch (error) { console.error("Update group error:", error); }
    };

    const handleSave = () => {
        if (localName !== currentGroup?.name) handleUpdateGroup({ name: localName });
    };

    const onStartGenerate = async () => {
        if (!currentGroup) return;
        if (!await modaler.confirm("确定开始生成视频吗？这可能需要几分钟。")) return;

        setIsGenerating(true);
        try {
            const res = await fetch("/api/generate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ groupId: currentGroup.id })
            });
            const data = await res.json();
            if (res.ok) {
                // Success
            } else if (data.error === "MULTIPLE_PENDING") {
                setIsConfirmModalOpen(true);
                setIsGenerating(false);
            } else {
                alert(`生成失败: ${data.message || data.error || "未知错误"}`);
                setIsGenerating(false);
            }
        } catch (error) {
            console.error("Generate error:", error);
            setIsGenerating(false);
        }
    };

    const segmentCount = currentGroup?.segments?.length || 0;
    const canGenerate = !isGenerating && !isSegmentsDirty && segmentCount > 0;
    const generateTitle = isSegmentsDirty
        ? "请先保存片段修改"
        : segmentCount === 0
            ? "请至少添加一个片段"
            : "开始生成视频";

    return (
        <div className="border-b border-zinc-200 dark:border-zinc-800 pb-4 transition-colors">
            <div className="flex flex-col md:flex-row items-end gap-6">
                <div className="flex-1 w-full space-y-2">
                    <label className="text-[10px] text-zinc-400 dark:text-zinc-500 uppercase font-black ml-1">创作名称</label>
                    <div className="flex group/input">
                        <input
                            type="text"
                            value={localName}
                            onChange={(e) => setLocalName(e.target.value)}
                            className="flex-1 bg-white dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700/50 rounded-none px-4 py-2 text-base font-bold text-zinc-900 dark:text-zinc-100 outline-none transition-all border-r-0 focus:border-blue-500/50"
                            placeholder="输入创作名称..."
                        />
                        <button
                            onClick={handleSave}
                            disabled={!hasChanges}
                            className={`px-4 flex items-center gap-2 border border-zinc-200 dark:border-zinc-700/50 rounded-none font-bold text-xs transition-all ${!hasChanges
                                ? "bg-zinc-50 dark:bg-zinc-800/30 text-zinc-400 dark:text-zinc-600 cursor-not-allowed border-l-zinc-200 dark:border-l-zinc-800/50"
                                : "bg-emerald-600/10 dark:bg-emerald-600/20 text-emerald-600 dark:text-emerald-400 border-l-emerald-200 dark:border-l-emerald-500/30 hover:bg-emerald-600/20 dark:hover:bg-emerald-600/30 hover:text-emerald-700 dark:hover:text-emerald-300"
                                }`}
                            title="保存修改"
                        >
                            <Save size={14} className={hasChanges ? "animate-pulse" : ""} />
                            <span>保存</span>
                        </button>
                    </div>
                </div>
                <div className="flex items-center gap-3 pb-1">
                    <button
                        onClick={onStartGenerate}
                        disabled={!canGenerate}
                        title={generateTitle}
                        className={`px-6 py-2 rounded-none font-black text-sm transition-all flex items-center gap-2 ${!canGenerate ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-600 cursor-not-allowed opacity-50 border border-zinc-200 dark:border-transparent" : "bg-blue-600 hover:bg-blue-500 text-white"}`}
                    >
                        {isGenerating ? <Loader2 size={16} className="animate-spin" /> : <Video size={16} />}
                        {isGenerating ? "生成中..." : "开始生成"}
                    </button>
                </div>
            </div>
        </div>
    );
};
